/** REMATRÍCULA 2027 · Wizard da família (DNA Connect Web + APP)
 *  Consome apenas /api/dnaconnect/rematricula/* — mesmo processo do DNA Matrículas. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, Loader2, Sparkles, GraduationCap, Utensils, Clock,
  Copy, Upload, ExternalLink, Instagram, Facebook, CreditCard, ShieldCheck, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import DocumentosMatricula from './DocumentosMatricula';

const brl = (v) => (v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const fam = () => localStorage.getItem('familyToken') || localStorage.getItem('dnaconnect_token') || '';
const P = () => ({ params: { token: fam() } });

const Card = ({ children, className = '', ...rest }) => (
  <div className={`rounded-2xl border border-violet-100 bg-white p-4 shadow-sm ${className}`} {...rest}>{children}</div>
);

const Steps = ({ step }) => (
  <div className="flex items-center gap-1.5 mb-4" data-testid="remat-steps">
    {['Aluno', 'Serviços', '2026×2027', 'Condição', 'Pagamento', 'Pronto'].map((l, i) => (
      <div key={l} className="flex-1">
        <div className={`h-1.5 rounded-full transition-colors ${i + 1 <= step ? 'bg-[#64327D]' : 'bg-violet-100'}`} />
        <div className={`mt-1 text-[9px] text-center ${i + 1 <= step ? 'text-[#64327D] font-medium' : 'text-violet-900/40'}`}>{l}</div>
      </div>
    ))}
  </div>
);

/** Tela final (matrícula EFETIVADA): nada de "acompanhe as próximas etapas" */
const MatriculaConcluida = ({ proc, docSummary, onBack }) => {
  const year = proc.campaign_year || 2027;
  const docs = proc.documents || docSummary;
  const ct = proc.contract || {};
  const pay = { confirmado: 'Confirmado', em_conferencia: 'Pendente de conferência', pendente: 'Pendente' }[proc.payment_status] || '—';
  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50/60 to-white pb-24" data-testid="remat-concluida">
      <div className="sticky top-0 z-20 flex items-center gap-3 border-b border-violet-100 bg-white/95 px-4 py-3 backdrop-blur">
        <button onClick={onBack} data-testid="remat-concluida-back" className="text-violet-900/70"><ArrowLeft className="h-5 w-5" /></button>
        <div className="min-w-0">
          <div className="truncate text-[15px] font-semibold text-violet-950">Matrícula {year}</div>
          <div className="truncate text-[11px] font-medium text-emerald-700" data-testid="remat-status">Matrícula concluída ✅</div>
        </div>
      </div>
      <div className="mx-auto max-w-2xl space-y-3 px-4 pt-4">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm" data-testid="remat-concluida-banner">
          <div className="flex items-center gap-2 text-[15px] font-bold text-emerald-800">
            <ShieldCheck className="h-5 w-5" /> ✅ MATRÍCULA {year} CONCLUÍDA
          </div>
          <p className="mt-2 whitespace-pre-line text-[13px] leading-relaxed text-emerald-900/85" data-testid="remat-concluida-msg">
            {proc.final_message
              || `A matrícula de ${proc.student_name} para ${proc.class_2027 || 'a turma do próximo ano'} foi concluída com sucesso. 💜`}
          </p>
        </div>

        <Card className="space-y-2">
          {[['Aluno', proc.student_name], [`Turma ${year}`, proc.class_2027], ['Turno', proc.shift],
            ['Situação', 'Matrícula concluída ✅']].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 border-b border-violet-50 pb-1.5 text-[13px]">
              <span className="text-violet-900/60">{k}</span><span className="text-right font-medium text-violet-950">{v || '—'}</span>
            </div>
          ))}
        </Card>

        <Card data-testid="remat-contrato">
          <div className="mb-1.5 flex items-center gap-2 text-[13px] font-semibold text-violet-950"><FileText className="h-4 w-4" /> Contrato</div>
          {ct.signed_in_person ? (
            <div className="flex items-center gap-1.5 text-[12.5px] text-emerald-700" data-testid="remat-contrato-presencial">
              <Check className="h-4 w-4" /> Contrato assinado presencialmente.
            </div>
          ) : ct.state === 'signed' ? (
            <div className="flex items-center gap-1.5 text-[12.5px] text-emerald-700"><Check className="h-4 w-4" /> Contrato assinado.</div>
          ) : ct.can_sign ? (
            <>
              <div className="text-[12.5px] text-violet-900/75">Disponível para assinatura</div>
              <a href="/familia/documentos" data-testid="remat-assinar-contrato"
                className="mt-2 flex h-11 items-center justify-center gap-2 rounded-xl bg-[#64327D] text-[13px] font-semibold text-white">
                Assinar contrato <ArrowRight className="h-4 w-4" />
              </a>
            </>
          ) : (
            <div className="text-[12.5px] text-violet-900/70" data-testid="remat-contrato-preparo">
              Contrato aguardando envio/assinatura — em preparação. Você receberá por e-mail e ele aparecerá aqui no APP.
            </div>
          )}
        </Card>

        <Card data-testid="remat-docs-status">
          <div className="mb-1 text-[13px] font-semibold text-violet-950">Documentação</div>
          <div className="text-[12.5px] text-violet-900/75">
            {docs ? `${docs.received}/${docs.total} recebidos` : '—'}
            {docs?.deferred ? ` · ${docs.deferred} para entregar depois` : ''}
          </div>
          <a href="/familia/documentos" className="mt-2 inline-block text-[12px] font-medium text-[#64327D]">Enviar/ver documentos</a>
        </Card>

        <Card data-testid="remat-pagamento-status">
          <div className="mb-1 text-[13px] font-semibold text-violet-950">Pagamento</div>
          <div className="text-[12.5px] text-violet-900/75">{pay}</div>
        </Card>

        <p className="px-1 text-[11.5px] text-violet-900/55">
          Você não precisa realizar nenhuma outra ação de efetivação neste momento.
        </p>
      </div>
    </div>
  );
};

/** Seleção dos filhos elegíveis (família com vários filhos) */
const Picker = ({ data, onPick }) => {
  const nav = useNavigate();
  const labels = data?.pipeline_labels || ['Turma', 'Serviços', 'Pagamento', 'Contrato', 'Assinatura', 'Conclusão'];
  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50/60 to-white pb-24" data-testid="remat-picker">
      <div className="sticky top-0 z-20 flex items-center gap-3 border-b border-violet-100 bg-white/95 px-4 py-3 backdrop-blur">
        <button onClick={() => nav('/familia')} data-testid="remat-picker-back" className="text-violet-900/70"><ArrowLeft className="h-5 w-5" /></button>
        <div className="min-w-0">
          <div className="truncate text-[15px] font-semibold text-violet-950">Rematrícula {data?.campaign?.year || 2027}</div>
          <div className="truncate text-[11px] text-violet-900/60">Escolha o aluno para iniciar ou continuar</div>
        </div>
      </div>
      <div className="mx-auto max-w-3xl px-4 pt-4">
        <div className="grid gap-3 md:grid-cols-2">
          {(data?.cards || []).map((c) => (
            <div key={c.student_id} className="min-w-0 overflow-hidden rounded-2xl border border-violet-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
              data-testid={`remat-pick-${c.student_id}`}>
              <div className="flex min-w-0 items-center gap-3">
                {c.photo_url ? (
                  <img src={c.photo_url} alt=""
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    className="h-14 w-14 shrink-0 rounded-xl object-cover ring-1 ring-violet-100" />
                ) : (
                  <div className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-[#64327D] text-[15px] font-semibold text-white">
                    {(c.student_name || '?').trim().split(/\s+/).slice(0, 2).map((n) => n[0]).join('').toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="truncate text-[14px] font-semibold text-violet-950">{c.student_name}</div>
                  <div className="truncate text-[11.5px] text-violet-900/60">Turma atual: <b className="text-violet-900/80">{c.class_2026 || '—'}</b></div>
                  <div className="truncate text-[11.5px] text-violet-900/60">Turma prevista: <b className="text-violet-900/80">{c.class_2027 || 'a definir'}</b></div>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1">
                {labels.map((l, i) => (
                  <div key={l} className={`h-1.5 flex-1 rounded-full ${i < (c.pipeline_stage || 0) ? 'bg-[#64327D]' : 'bg-violet-100'}`} />
                ))}
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[11.5px] font-medium text-violet-900/70">{c.status_label}</span>
                <button onClick={() => onPick(c)} data-testid={`remat-cta-${c.student_id}`}
                  className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[#64327D] px-4 text-[12.5px] font-semibold text-white transition-transform hover:scale-[1.03] active:scale-95">
                  {c.completed ? 'Ver detalhes' : c.cta} <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
        {!data && <div className="grid min-h-[40vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#64327D]" /></div>}
        {data && !(data.cards || []).length && (
          <Card className="mt-4 text-center text-sm text-violet-900/70">Nenhum aluno elegível para a rematrícula neste momento.</Card>
        )}
        {(data?.ineligible || []).length > 0 && (
          <Card className="mt-3" data-testid="remat-ineligible">
            <p className="text-[11px] uppercase tracking-wide text-violet-900/50">Não elegíveis nesta campanha</p>
            {data.ineligible.map((c) => (
              <div key={c.student_id} className="mt-1.5 text-[12.5px] text-violet-900/70">
                <b className="text-violet-950">{c.student_name}</b> — {c.reason}
              </div>
            ))}
            <p className="mt-2 text-[11.5px] text-violet-900/50">Fale com a Secretaria para regularizar.</p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default function Rematricula2027() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [proc, setProc] = useState(null);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [integral, setIntegral] = useState(false);
  const [almoco, setAlmoco] = useState(false);
  const [segment, setSegment] = useState(0);
  const segTouched = React.useRef(false);
  const [declared, setDeclared] = useState(false);
  const [docSummary, setDocSummary] = useState(null);

  const pid = sp.get('process_id');
  const studentId = sp.get('student_id');

  const load = useCallback(async (id) => {
    const { data } = await api.get(`/dnaconnect/rematricula/process/${id}`, P());
    setProc(data);
    setIntegral(!!data.integral_interest);
    setAlmoco(!!data.almoco_interest);
    setDeclared(!!data.promo_declaration);
    // Ensino Médio não tem etapa de Período Integral
    setStep(Math.max(1, data.step || 1) === 2 && data.integral_allowed === false
      ? 3 : Math.max(1, data.step || 1));
    return data;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (pid) return void (await load(pid));
        if (studentId) {
          const { data } = await api.post(`/dnaconnect/rematricula/${studentId}/start`, null, P());
          await load(data.process_id);
        }
      } catch (e) { toast.error(e?.response?.data?.detail || 'Não foi possível abrir a rematrícula'); }
    })();
  }, [pid, studentId, load]);

  // Sem aluno na URL → seleção dos filhos elegíveis
  const [cards, setCards] = useState(null);
  useEffect(() => {
    if (pid || studentId) return;
    api.get('/dnaconnect/rematricula/cards', P()).then(({ data }) => setCards(data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Não foi possível carregar seus filhos'));
  }, [pid, studentId]);

  // Resumo documental na conclusão (nunca bloqueia a matrícula)
  useEffect(() => {
    if (!proc?.process_id || step < 6) return;
    api.get(`/dnaconnect/matriculas/${proc.process_id}/documentos`, P())
      .then(({ data }) => setDocSummary(data.summary)).catch(() => {});
  }, [proc?.process_id, step]);

  const segments = proc?.price_table?.segments || [];  // Abre o comparativo já no segmento do aluno (Infantil / Fund I / Fund II / Médio)
  useEffect(() => {
    if (segTouched.current || !segments.length || !proc?.segment_key) return;
    const i = segments.findIndex((s) => s.key === proc.segment_key);
    if (i >= 0) { setSegment(i); segTouched.current = true; }
  }, [segments, proc?.segment_key]);

  const comparativo = useMemo(() => {
    const seg = segments[segment];
    if (!seg) return [];
    return seg.items
      .filter(it => proc?.integral_allowed !== false
        || !/integral|almo/i.test(String(it.label || '')))   // Ensino Médio não vê Integral/Almoço
      .map(it => {
        const dif = (it.y2027 ?? 0) - (it.y2026 ?? 0);
        const pct = it.y2026 ? (dif / it.y2026) * 100 : 0;
        return { ...it, dif, pct };
      });
  }, [segments, segment, proc?.integral_allowed]);

  const saveServices = async (goto = 3) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dnaconnect/rematricula/process/${proc.process_id}/services`,
        { integral: proc.integral_allowed === false ? false : integral,
          almoco: proc.integral_allowed === false ? false : almoco, extra_keys: [] }, P());
      setProc(data ?? proc);
      setStep(goto);
    }
    catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao salvar os serviços'); }
    finally { setBusy(false); }
  };

  // V9.27 · PIX pelo endpoint FAMILIAR (nunca rota admin). O valor vem do backend.
  const choosePromo = async (promo) => {
    if (promo.requires_declaration && !declared) return toast.error('Confirme a declaração para liberar o PIX.');
    setBusy(true); setBusyKey(promo.key);
    try {
      if (promo.method === 'presencial') {
        const { data } = await api.post(`/dnaconnect/rematricula/process/${proc.process_id}/promo`,
          { promo_key: promo.key, declaration: declared }, P());
        setProc(data); setStep(5);
        toast.success('Condição registrada. Finalize o cartão na Secretaria.');
        return;
      }
      await api.post(`/dnaconnect/matriculas/${proc.process_id}/pix`,
        { promotion_id: promo.key, declaration: declared }, P());
      const { data } = await api.get(`/dnaconnect/rematricula/process/${proc.process_id}`, P());
      setProc(data); setStep(5);
      toast.success('PIX gerado com o valor da sua condição.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível gerar o PIX agora.');
    } finally { setBusy(false); setBusyKey(null); }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file); fd.append('token', fam());
    setBusy(true);
    try {
      const { data } = await api.post(`/dnaconnect/rematricula/process/${proc.process_id}/receipt`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(data.message); await load(proc.process_id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao enviar o comprovante'); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dnaconnect/rematricula/process/${proc.process_id}/confirm`, null, P());
      setProc(data); setStep(6);
      toast.success('Pedido de rematrícula confirmado! A Secretaria dará continuidade.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao confirmar'); }
    finally { setBusy(false); }
  };

  if (!pid && !studentId) {
    return <Picker data={cards} onPick={(c) => nav(c.process_id
      ? `/familia/rematricula?process_id=${c.process_id}`
      : `/familia/rematricula?student_id=${c.student_id}`)} />;
  }

  if (!proc) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#64327D]" /></div>;

  if (proc.effectivated) return <MatriculaConcluida proc={proc} docSummary={docSummary} onBack={() => nav('/familia')} />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50/60 to-white pb-24" data-testid="rematricula-wizard">
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-violet-100 px-4 py-3 flex items-center gap-3">
        <button onClick={() => nav(-1)} data-testid="remat-back" className="text-violet-900/70"><ArrowLeft className="w-5 h-5" /></button>
        <div className="min-w-0">
          <div className="text-[15px] font-semibold text-violet-950 truncate">Rematrícula {proc.campaign_year || 2027}</div>
          <div className="text-[11px] text-violet-900/60 truncate" data-testid="remat-status">{proc.status_label}</div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-4">
        <Steps step={step} />

        {step === 1 && (
          <Card className="space-y-3" data-testid="remat-step1">
            <div className="flex items-center gap-3 text-[#64327D] font-semibold">
              {proc.photo_url
                ? <img src={proc.photo_url} alt="" data-testid="remat-student-photo"
                    className="h-12 w-12 rounded-xl object-cover ring-1 ring-violet-100" />
                : <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#64327D] text-[14px] text-white">
                    <GraduationCap className="h-5 w-5" /></span>}
              <span>Seu aluno</span>
            </div>
            {[['Aluno', proc.student_name], [`Turma atual (${(proc.campaign_year || 2027) - 1})`, proc.class_2026],
              [`Turma prevista (${proc.campaign_year || 2027})`, proc.class_2027], ['Turno', proc.shift]].map(([k, v]) => (
              <div key={k} className="flex justify-between text-[13px] border-b border-violet-50 pb-1.5">
                <span className="text-violet-900/60">{k}</span><span className="font-medium text-violet-950">{v || '—'}</span>
              </div>
            ))}
            <button onClick={() => { if (proc.integral_allowed === false) { saveServices(3); } else { setStep(2); } }} data-testid="remat-next-1"
              className="w-full h-11 rounded-xl bg-[#64327D] text-white font-medium flex items-center justify-center gap-2">
              Continuar <ArrowRight className="w-4 h-4" />
            </button>
          </Card>
        )}

        {step === 2 && (
          <Card className="space-y-4" data-testid="remat-step2">
            <div className="flex items-center gap-2 text-[#64327D] font-semibold"><Clock className="w-4 h-4" /> Serviços 2027</div>
            <div className="text-[13px] text-violet-950 font-medium">
              Você tem interesse no Período Integral para {proc.campaign_year || 2027}?
            </div>
            <div className="flex gap-2">
              {[['Sim, tenho interesse', true], ['Não tenho interesse', false]].map(([l, v]) => (
                <button key={l} data-testid={`remat-integral-${v ? 'sim' : 'nao'}`}
                  onClick={() => { setIntegral(v); if (!v) setAlmoco(false); }}
                  className={`flex-1 h-11 rounded-xl border px-2 text-[12.5px] font-medium leading-tight ${integral === v
                    ? 'bg-[#64327D] text-white border-[#64327D]' : 'border-violet-200 text-violet-900/70'}`}>{l}</button>
              ))}
            </div>
            <div className="rounded-xl bg-amber-50 px-3 py-2 text-[11.5px] text-amber-800" data-testid="remat-integral-aviso">
              ⚠️ As vagas para o Período Integral são limitadas e a confirmação está sujeita à
              disponibilidade da escola.
            </div>
            {integral && (
              <label className="flex items-center gap-2 text-[13px] text-violet-950" data-testid="remat-almoco">
                <input type="checkbox" checked={almoco} onChange={e => setAlmoco(e.target.checked)} />
                <Utensils className="w-4 h-4 text-[#1FA34A]" /> Incluir Almoço
              </label>
            )}
            {(proc.services_catalog || []).filter(s => !/integral|almo/i.test(s.key || '')).length > 0 && (
              <div className="text-[11px] text-violet-900/50">Outros serviços do segmento serão confirmados pela Secretaria.</div>
            )}
            <button disabled={busy} onClick={() => saveServices(3)} data-testid="remat-next-2"
              className="w-full h-11 rounded-xl bg-[#64327D] text-white font-medium disabled:opacity-60">
              {busy ? 'Salvando…' : 'Continuar'}
            </button>
          </Card>
        )}

        {step === 3 && (
          <Card>
            <div className="flex items-center gap-2 text-[#64327D] font-semibold mb-3" data-testid="remat-step3"><Sparkles className="w-4 h-4" /> Comparativo {(proc.campaign_year || 2027) - 1} × {proc.campaign_year || 2027}</div>
            <div className="flex gap-1.5 overflow-x-auto pb-2 mb-2">
              {segments.map((s, i) => (
                <button key={s.key} onClick={() => { segTouched.current = true; setSegment(i); }} data-testid={`remat-seg-${s.key}`}
                  className={`px-3 h-8 rounded-full text-[11px] whitespace-nowrap border ${i === segment
                    ? 'bg-[#64327D] text-white border-[#64327D]' : 'border-violet-200 text-violet-900/70'}`}>{s.label}</button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11.5px]" data-testid="remat-table">
                <thead><tr className="text-violet-900/50 text-left">
                  <th className="py-1.5">Item</th><th>2026</th><th>2027</th><th>Dif.</th><th>%</th></tr></thead>
                <tbody>
                  {comparativo.map(it => (
                    <tr key={it.label} className="border-t border-violet-50">
                      <td className="py-1.5 pr-2 text-violet-950">{it.label}</td>
                      <td className="text-violet-900/60">{brl(it.y2026)}</td>
                      <td className="font-medium text-violet-950">{brl(it.y2027)}</td>
                      <td className={it.dif > 0 ? 'text-amber-600' : 'text-emerald-600'}>{it.dif ? brl(it.dif) : '—'}</td>
                      <td className={it.dif > 0 ? 'text-amber-600' : 'text-emerald-600'}>{it.dif ? `${it.pct.toFixed(1)}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={() => setStep(4)} data-testid="remat-next-3"
              className="mt-4 w-full h-11 rounded-xl bg-[#64327D] text-white font-medium">Escolher minha condição</button>
          </Card>
        )}

        {step === 4 && (
          <div className="space-y-3" data-testid="remat-step4">
            <div className="text-[13px] font-semibold text-violet-950 px-1">Escolha sua condição de Rematrícula</div>
            {(proc.promotions || []).map(pr => (
              <Card key={pr.key} data-testid={`remat-promo-${pr.key}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[14px] font-semibold text-violet-950">{pr.label}</div>
                    <div className="text-[11.5px] text-violet-900/60 mt-0.5">{pr.description}</div>
                    {pr.deadline && <div className="text-[11px] text-[#1FA34A] mt-1">Até {new Date(pr.deadline + 'T12:00').toLocaleDateString('pt-BR')}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    {pr.installments
                      ? <div className="text-[15px] font-bold text-[#64327D]">{pr.installments}x {brl(pr.installment_amount)}</div>
                      : <><div className="text-[11px] text-violet-900/40 line-through">{brl(pr.base)}</div>
                        <div className="text-[17px] font-bold text-[#64327D]">{brl(pr.amount)}</div></>}
                  </div>
                </div>
                {(pr.links || []).length > 0 && (
                  <div className="flex gap-2 mt-3">
                    {pr.links.map(l => (
                      <a key={l.url} href={l.url} target="_blank" rel="noreferrer" data-testid={`remat-link-${/insta/i.test(l.label) ? 'ig' : 'fb'}`}
                        className="flex-1 h-9 rounded-xl border border-violet-200 text-[12px] text-violet-900/80 flex items-center justify-center gap-1.5">
                        {/insta/i.test(l.label) ? <Instagram className="w-3.5 h-3.5" /> : <Facebook className="w-3.5 h-3.5" />}
                        {l.label} <ExternalLink className="w-3 h-3" />
                      </a>
                    ))}
                  </div>
                )}
                {pr.requires_declaration && (
                  <label className="flex items-start gap-2 mt-3 text-[12px] text-violet-950" data-testid="remat-declaration">
                    <input type="checkbox" checked={declared} onChange={e => setDeclared(e.target.checked)} className="mt-0.5" />
                    {pr.declaration_text}
                  </label>
                )}
                {pr.method === 'presencial' && (
                  <div className="mt-3 rounded-xl bg-amber-50 text-amber-800 text-[11.5px] px-3 py-2 flex gap-2">
                    <CreditCard className="w-4 h-4 shrink-0" /> {pr.notice}
                  </div>
                )}
                <button disabled={busy} onClick={() => choosePromo(pr)} data-testid={`remat-choose-${pr.key}`}
                  className="mt-3 w-full h-11 rounded-xl bg-[#64327D] text-white font-medium disabled:opacity-60">
                  {busyKey === pr.key
                    ? (pr.method === 'presencial' ? 'Registrando…' : 'Gerando PIX…')
                    : pr.method === 'presencial' ? 'Escolher e finalizar na Secretaria' : 'Escolher e gerar PIX'}
                </button>
              </Card>
            ))}
            {proc.terms && <div className="text-[11px] text-violet-900/55 px-1">{proc.terms}</div>}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-3" data-testid="remat-step5">
            {proc.pix ? (
              <Card className="text-center">
                <div className="text-[13px] font-semibold text-violet-950">PIX da sua rematrícula</div>
                <div className="text-[24px] font-bold text-[#64327D] my-1">{brl(proc.pix.amount)}</div>
                {proc.pix.qr_code && <img src={proc.pix.qr_code.startsWith('data:') ? proc.pix.qr_code : `data:image/png;base64,${proc.pix.qr_code}`}
                  alt="QR Code PIX" className="w-44 h-44 mx-auto my-2" data-testid="remat-qr" />}
                <div className="text-[11.5px] text-violet-900/60">{proc.pix.beneficiary}<br />Chave: {proc.pix.key}<br />{proc.pix.reference}</div>
                {proc.pix.copy_paste && (
                  <button data-testid="remat-copy-pix" onClick={() => { navigator.clipboard.writeText(proc.pix.copy_paste); toast.success('PIX copiado!'); }}
                    className="mt-3 w-full h-11 rounded-xl bg-[#64327D] text-white font-medium flex items-center justify-center gap-2">
                    <Copy className="w-4 h-4" /> Copiar PIX
                  </button>
                )}
              </Card>
            ) : (
              <Card className="text-[12.5px] text-amber-800 bg-amber-50 border-amber-200 flex gap-2">
                <CreditCard className="w-4 h-4 shrink-0" /> Pagamento no cartão realizado presencialmente na Secretaria do Novo DNA.
              </Card>
            )}
            <Card>
              <div className="text-[13px] font-semibold text-violet-950 mb-2">Comprovante</div>
              <label className="h-11 rounded-xl border border-dashed border-violet-300 flex items-center justify-center gap-2 text-[12.5px] text-violet-900/70 cursor-pointer" data-testid="remat-upload">
                <Upload className="w-4 h-4" /> {proc.receipt_sent ? 'Enviar outro comprovante' : 'Enviar comprovante (JPG, PNG ou PDF)'}
                <input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={e => upload(e.target.files?.[0])} />
              </label>
              {proc.receipt_sent && <div className="mt-2 text-[12px] text-emerald-700 flex items-center gap-1.5" data-testid="remat-receipt-ok">
                <Check className="w-4 h-4" /> Comprovante recebido. A Secretaria fará a conferência para formalização da rematrícula.</div>}
              {proc.whatsapp && (
                <a href={`https://wa.me/${String(proc.whatsapp).replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
                  data-testid="remat-whats" className="mt-2 block text-center text-[12px] text-[#1FA34A]">Encaminhar também pelo WhatsApp</a>
              )}
            </Card>
            <button onClick={() => setStep(6)} data-testid="remat-next-5"
              className="w-full h-11 rounded-xl border border-[#64327D] text-[#64327D] font-medium">Ver resumo</button>
          </div>
        )}

        {step === 6 && (
          <Card className="space-y-2">
            <div className="flex items-center gap-2 text-[#64327D] font-semibold" data-testid="remat-step6"><FileText className="w-4 h-4" /> Resumo do pedido</div>
            {[['Aluno', proc.student_name], [`Turma ${proc.campaign_year || 2027}`, proc.class_2027], ['Turno', proc.shift],
              ...(proc.integral_allowed === false ? [] : [
                ['Interesse no Período Integral', integral ? 'Sim · sujeito à disponibilidade' : 'Não'],
                ['Interesse no Almoço', almoco ? 'Sim · sujeito à disponibilidade' : 'Não']]),
              ['Condição', proc.promo?.label], ['Valor da rematrícula', brl(proc.promo?.amount)],
              ['Forma de pagamento', proc.promo?.method === 'presencial' ? 'Cartão na Secretaria' : 'PIX'],
              ['Comprovante', proc.receipt_sent ? 'Enviado' : 'Pendente'],
              ['Situação', proc.status_label]].map(([k, v]) => (
              <div key={k} className="flex justify-between text-[13px] border-b border-violet-50 pb-1.5">
                <span className="text-violet-900/60">{k}</span><span className="font-medium text-violet-950 text-right">{v || '—'}</span>
              </div>
            ))}
            {proc.terms && <div className="text-[11px] text-violet-900/55 pt-1">{proc.terms}</div>}
            {docSummary && (
              <div className="rounded-xl bg-violet-50 border border-violet-100 px-3 py-2.5 text-[12.5px] text-violet-900" data-testid="remat-doc-summary">
                <b>Documentação:</b> {docSummary.received}/{docSummary.total} recebidos
                {docSummary.deferred ? ` · ${docSummary.deferred} pendência(s) que podem ser entregues posteriormente` : ''}
                {docSummary.missing_required ? ` · ${docSummary.missing_required} faltando` : ''}
                <br /><span className="text-violet-900/60">Isso não impede a efetivação da sua matrícula.</span>
              </div>
            )}
            {proc.confirmed_at ? (
              <div className="rounded-xl bg-emerald-50 text-emerald-700 text-[12.5px] px-3 py-3 flex items-center gap-2" data-testid="remat-confirmed">
                <ShieldCheck className="w-4 h-4" /> Pedido confirmado! Acompanhe por aqui as próximas etapas (conferência, contrato e assinatura).
              </div>
            ) : (
              <button disabled={busy} onClick={confirm} data-testid="remat-confirm"
                className="w-full h-12 rounded-xl bg-[#64327D] text-white font-semibold disabled:opacity-60">
                {busy ? 'Confirmando…' : 'Confirmar pedido de Rematrícula'}
              </button>
            )}
            <button onClick={() => setStep(7)} data-testid="remat-go-docs"
              className="w-full h-11 rounded-xl border border-violet-200 text-[13px] font-medium text-violet-800">
              Documentos da matrícula
            </button>
          </Card>
        )}

        {step === 7 && (
          <>
            <button onClick={() => setStep(6)} data-testid="remat-docs-back"
              className="mb-2 text-[12.5px] font-medium text-violet-800">← Voltar ao resumo</button>
            <DocumentosMatricula processId={proc.process_id} studentName={proc.student_name} />
          </>
        )}
      </div>
    </div>
  );
}

/** Faixa da Rematrícula dentro do card de cada filho elegível (tela "Meus Filhos") */
let _cardsPromise = null;
export const RematriculaFilhoCta = ({ studentId }) => {
  const nav = useNavigate();
  const [card, setCard] = useState(null);
  const [year, setYear] = useState(null);
  useEffect(() => {
    if (!_cardsPromise) {
      _cardsPromise = api.get('/dnaconnect/rematricula/cards', { params: { token: fam() } })
        .then(({ data }) => data)
        .catch((e) => { console.error('[Rematrícula] cards', e?.response?.data || e); return null; });
    }
    _cardsPromise.then((d) => {
      if (!d) return;
      setYear(d.campaign?.year || 2027);
      setCard((d.cards || []).find((c) => c.student_id === studentId) || null);
    });
  }, [studentId]);
  if (!card) return null;
  return (
    <button data-testid={`remat-filho-cta-${studentId}`}
      onClick={(e) => {
        e.stopPropagation();
        nav(card.process_id
          ? `/familia/rematricula?process_id=${card.process_id}`
          : `/familia/rematricula?student_id=${card.student_id}`);
      }}
      className="mt-3 flex w-full items-center justify-between gap-2 rounded-xl bg-[#3F1F5C] px-3 py-2.5 text-left text-white transition-transform hover:scale-[1.01] active:scale-95">
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-[9.5px] font-medium uppercase tracking-[0.18em] text-white/60">
          <Sparkles className="h-3 w-3 text-[#F0B429]" /> Rematrícula {year}
        </span>
        <span className="mt-0.5 block truncate text-[12.5px] font-semibold">{card.cta}</span>
        <span className="block truncate text-[10.5px] text-white/65">{card.status_label}</span>
      </span>
      <ArrowRight className="h-4 w-4 shrink-0" />
    </button>
  );
};

/** Card premium da HOME (DNA Connect Web + APP) */
export const RematriculaHomeCard = () => {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const fetchCards = useCallback(() => {
    setErr(null);
    api.get('/dnaconnect/rematricula/cards', { params: { token: fam() } })
      .then(({ data }) => setData(data))
      .catch((e) => {
        console.error('[Rematrícula] falha ao carregar os cards', e?.response?.data || e);
        setErr(e?.response?.data?.detail || 'Não foi possível carregar a Rematrícula agora.');
      });
  }, []);
  useEffect(() => { fetchCards(); }, [fetchCards]);

  if (err) {
    return (
      <div className="rounded-2xl border border-violet-200 bg-white p-4" data-testid="rematricula-home-error">
        <div className="text-sm font-medium text-violet-950">Rematrícula 2027</div>
        <p className="mt-1 text-[12.5px] text-slate-600">{err}</p>
        <button onClick={fetchCards} data-testid="remat-retry"
          className="mt-2 h-9 rounded-full bg-[#64327D] px-4 text-[12.5px] font-semibold text-white">
          Tentar novamente
        </button>
      </div>
    );
  }
  if (!data?.campaign || !(data.cards || []).length) return null;
  const year = data.campaign.year || 2027;
  const labels = data.pipeline_labels || ['Turma', 'Serviços', 'Pagamento', 'Contrato', 'Assinatura', 'Conclusão'];
  const andamento = data.cards.filter((c) => c.in_progress);
  const concluidas = data.cards.filter((c) => c.completed);
  const emAndamento = andamento.length > 0;

  return (
    <div className="relative overflow-hidden rounded-2xl bg-[#3F1F5C] p-5 md:p-6 text-white shadow-xl ring-1 ring-white/10"
      data-testid="rematricula-home-card">
      <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-[#8E52AB]/40 blur-3xl" />
      <div className="pointer-events-none absolute -left-16 -bottom-10 h-40 w-40 rounded-full bg-[#F0B429]/20 blur-3xl" />
      <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10.5px] font-medium uppercase tracking-[0.2em] text-white/60">
            <Sparkles className="h-3.5 w-3.5 text-[#F0B429]" /> Novo DNA · {year}
          </div>
          <div className="mt-1.5 text-base font-semibold uppercase tracking-wide md:text-lg" data-testid="remat-home-title">
            Rematrícula {year}
          </div>
          <p className="mt-1 max-w-md text-sm text-white/75">
            {emAndamento
              ? 'Rematrícula em andamento — acompanhe e finalize as próximas etapas.'
              : 'Garanta a continuidade do seu filho no Novo DNA.'}
          </p>
        </div>
        <button onClick={() => nav('/familia/rematricula')} data-testid="remat-open-btn"
          className="group inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-full bg-white px-5 text-[13px] font-semibold text-[#3F1F5C] shadow-lg transition-transform hover:scale-[1.03] active:scale-95">
          {emAndamento ? 'Continuar Rematrícula' : 'Efetuar Rematrícula'}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>

      {(emAndamento || concluidas.length > 0) && (
        <div className="relative mt-4 grid gap-2 md:grid-cols-2" data-testid="remat-home-progress">
          {[...andamento, ...concluidas].map((c) => (
            <div key={c.student_id} className="rounded-xl bg-white/10 px-3 py-2.5 backdrop-blur"
              data-testid={`remat-card-${c.student_id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-2">
                  {c.photo_url && <img src={c.photo_url} alt=""
                    className="h-6 w-6 shrink-0 rounded-full object-cover ring-1 ring-white/30" />}
                  <span className="truncate text-[13px] font-medium">{c.student_name}</span>
                </span>
                <span className="shrink-0 text-[10.5px] text-white/70">{c.status_label}</span>
              </div>
              <div className="mt-2 flex items-center gap-1">
                {labels.map((l, i) => (
                  <div key={l} className="flex-1" title={l}>
                    <div className={`h-1.5 rounded-full ${i < (c.pipeline_stage || 0) ? 'bg-[#F0B429]' : 'bg-white/20'}`} />
                  </div>
                ))}
              </div>
              <div className="mt-1 flex justify-between text-[9.5px] text-white/55">
                <span>{labels[0]}</span>
                <span className="font-medium text-white/80">{c.pipeline_label || '—'}</span>
                <span>{labels[labels.length - 1]}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
